import { Shield, Check, X as XIcon, Eye } from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const roles = [
    { key: 'admin', label: 'Admin', desc: 'Full system access' },
    { key: 'management', label: 'Management', desc: 'View all data, manage quotes, override prices' },
    { key: 'sales', label: 'Sales', desc: 'View commercial data, manage quotes' },
    { key: 'operations', label: 'Operations', desc: 'View operational & packaging data' },
];

const permissionGroups = [
    {
        group: 'Products',
        perms: [
            { label: 'View product catalog', admin: true, management: true, sales: true, operations: true },
            { label: 'Edit products', admin: true, management: false, sales: false, operations: false },
            { label: 'Export products (CSV/Excel)', admin: true, management: true, sales: true, operations: false },
            { label: 'View costing', admin: true, management: true, sales: false, operations: false },
        ]
    },
    {
        group: 'Quotations',
        perms: [
            { label: 'View all quotes', admin: true, management: true, sales: false, operations: true },
            { label: 'View own quotes', admin: true, management: true, sales: true, operations: false },
            { label: 'Create quotes', admin: true, management: true, sales: true, operations: false },
            { label: 'Edit all quotes', admin: true, management: true, sales: false, operations: false },
            { label: 'Edit own quotes', admin: true, management: true, sales: true, operations: false },
            { label: 'Delete quotes', admin: true, management: false, sales: false, operations: false },
            { label: 'Override price', admin: true, management: true, sales: true, operations: false },
            { label: 'Export quote PDF/CSV', admin: true, management: true, sales: true, operations: false },
            { label: 'Duplicate quote', admin: true, management: true, sales: true, operations: false },
        ]
    },
    {
        group: 'Custom Products',
        perms: [
            { label: 'View custom products', admin: true, management: true, sales: true, operations: true },
            { label: 'Create custom products', admin: true, management: true, sales: true, operations: false },
            { label: 'Edit custom products', admin: true, management: true, sales: true, operations: false },
            { label: 'Delete custom products', admin: true, management: false, sales: false, operations: false },
        ]
    },
    {
        group: 'Delivery Challans',
        perms: [
            { label: 'View challans', admin: true, management: true, sales: true, operations: true },
            { label: 'Create challans', admin: true, management: true, sales: true, operations: false },
            { label: 'Edit challans', admin: true, management: true, sales: true, operations: false },
            { label: 'Delete challans', admin: true, management: false, sales: false, operations: false },
            { label: 'Export challans (PDF/CSV)', admin: true, management: true, sales: true, operations: true },
            { label: 'Duplicate challans', admin: true, management: true, sales: false, operations: false },
        ]
    },
    {
        group: 'Tools',
        perms: [
            { label: 'Use Sheet Optimizer', admin: true, management: true, sales: true, operations: true },
            { label: 'View dashboard', admin: true, management: true, sales: true, operations: true },
        ]
    },
    {
        group: 'Data Management',
        perms: [
            { label: 'Import CSV data', admin: true, management: false, sales: false, operations: false },
            { label: 'Import product images', admin: true, management: false, sales: false, operations: false },
            { label: 'Match/dismiss unmatched images', admin: true, management: false, sales: false, operations: false },
            { label: 'Manage categories', admin: true, management: false, sales: false, operations: false },
        ]
    },
    {
        group: 'Administration',
        perms: [
            { label: 'Manage users', admin: true, management: false, sales: false, operations: false },
        ]
    },
];

function PermIcon({ allowed }) {
    return allowed
        ? <Check size={16} weight="bold" className="text-emerald-600 dark:text-emerald-400" />
        : <XIcon size={14} className="text-muted-foreground/30" />;
}

export default function RolePermissions() {
    return (
        <div className="space-y-6" data-testid="role-permissions-page">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Role Permissions</h1>
                <p className="text-sm text-muted-foreground mt-1">Reference of what each role can access and perform</p>
            </div>

            {/* Role cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {roles.map(r => (
                    <div key={r.key} className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <Shield size={18} className="text-primary" />
                            <span className="font-semibold text-sm capitalize">{r.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{r.desc}</p>
                    </div>
                ))}
            </div>

            {/* Permission matrix */}
            {permissionGroups.map(g => (
                <div key={g.group} className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="px-5 py-3 border-b border-border bg-muted/30">
                        <h3 className="font-semibold text-sm">{g.group}</h3>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[45%]">Permission</TableHead>
                                {roles.map(r => (
                                    <TableHead key={r.key} className="text-center capitalize text-xs">{r.label}</TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {g.perms.map((p, i) => (
                                <TableRow key={i}>
                                    <TableCell className="text-sm">{p.label}</TableCell>
                                    {roles.map(r => (
                                        <TableCell key={r.key} className="text-center">
                                            <PermIcon allowed={p[r.key]} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ))}
        </div>
    );
}
